import { Component, Input } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { CommonService } from 'src/app/shared/services/common.service';
@Component({
	selector: 'app-score-chart',
	templateUrl: './score-chart.component.html',
	styleUrls: ['./score-chart.component.scss']
})
export class ScoreChartComponent {
	@Input() data: any = {};
	@Input() devices: any = {};
	@Input() hasTruckTypeFilter = false;
	@Input() hasVoltageFilter = false;
	@Input() hasCapacityFilter = false;
	@Input() hasWorkingDaysFilter = true;
	@Input() range: any = null;
	@Input() chartType: string = null;
	@Input() chartMaxValue: number = 100;
	@Input() daysFilterSelected: string = '';
	@Input() periodSelected: string = '';
	@Input() truckTypeSelected: string = '';
	@Input() hideFilters: boolean = false;
	@Input() totalTrucks!: number;
	value = 0;

	periods = [
		{ value: 'last_7_days', label: this.translateService.instant('durations.last_7_days') },
		{ value: 'last_14_days', label: this.translateService.instant('durations.last_14_days') },
		{ value: 'last_30_days', label: this.translateService.instant('durations.last_30_days') },
		{ value: 'last_90_days', label: this.translateService.instant('durations.last_90_days') },
		{ value: 'year_to_date', label: this.translateService.instant('durations.year_to_date') },
		{ value: 'last_year', label: this.translateService.instant('durations.last_year') },
		{ value: 'since_installation', label: this.translateService.instant('durations.all_years') },
	];

	daysFilters = [
		{ value: 'allDays', label: this.translateService.instant('g.all_days') },
		{ value: 'workingDays', label: this.translateService.instant('g.workings_days') },
	];

	truckTypes = [];
	selectedPeriod = this.periods[0].value;
	selectedTruckType = "all";
	selectedDaysFilter = this.daysFilters[0].value;

	batteryVoltagesOptions: any[] = [];
	selectedBatteryVoltage: string = 'all';
	batteryCapacitiesOptions: any[] = [];
	selectedBatteryCapacity: string = 'all';

	valuePx = 0;
	valueStatus = 'Very Low';
	statusColor = '#bf0001'; // Default color for 'Very Low' status
	textColor = '#ffffff';
	ranges = [
		{from: 0, to: 10, color: '#bf0001', status: 'Very Low', width: 0, textColor: '#ffffff'},
		{from: 10, to: 30, color: '#ffc000', status: 'Low', width: 0, textColor: '#000000'},
		{from: 30, to: 60, color: '#feff99', status: 'Medium', width: 0, textColor: '#000000'},
		{from: 60, to: 80, color: '#92d051', status: 'Good', width: 0, textColor: '#000000'},
		{from: 80, to: 100, color: '#0e7c41', status: 'Excellent', width: 0, textColor: '#ffffff'},
	];
	rangesLabels = [];
	chartWidthPx = 600;

	constructor(
		private translateService: TranslateService,
		private commonService: CommonService,
	) {}

	ngOnInit() {
		this.prepareRanges();
		this.prepareFilters();
		this.formatSelectedData();
		this.updateChart();
	}

	ngOnChanges(changes){
		if(changes.data && this.data) {
			this.prepareRanges();
			this.prepareFilters();
			this.formatSelectedData();
			this.updateChart();
		}
		if (changes?.['daysFilterSelected'] || changes?.['periodSelected'] || changes?.['truckTypeSelected']){
			this.selectedDaysFilter = this.daysFilterSelected;
			this.selectedPeriod = this.periodSelected;
			this.selectedTruckType = this.truckTypeSelected;
			this.formatSelectedData();
			this.updateChart();
		}
	}

	prepareRanges() {
		if (this.range)
			this.ranges = this.range;

		this.rangesLabels = ['0%'];
		const maxValue = this.ranges[this.ranges.length - 1].to;
		const rangeStep = maxValue / 5;
		let counter = 1;

		for (const range of this.ranges) {
			this.rangesLabels.push(`${rangeStep * counter}%`);
			range.width = (range.to - range.from) * this.chartWidthPx / maxValue;
			counter++;
		}
	}

	onSelectionChanged(event) {
		switch (event.filter) {
			case 'period':
				this.selectedPeriod = event.value;
				break;
			case 'truckType':
				this.selectedTruckType = event.value;
				break;
			case 'daysFilter':
				this.selectedDaysFilter = event.value;
				break;
			case 'voltageFilter':
				this.selectedBatteryVoltage = event.value;
				break;
			case 'capacityFilter':
				this.selectedBatteryCapacity = event.value;
				break;
			default:
				break;
		}

		this.formatSelectedData();
		this.updateChart();
	}

	prepareFilters() {
		this.truckTypes = [{value: 'all', label: 'all'}];

		for (const macAddress in this.devices) {
			const device = this.devices[macAddress];

			if (device.truck_type) {
				const isExist = this.truckTypes.find((type) => type.value == device.truck_type);
				if (!isExist)
					this.truckTypes.push({
						value: device.truck_type,
						label: device.truck_type,
					});
			}
		}

		const batteriesCapacities = [];
		const batteriesVoltages = [];

		for (const device in this.devices) {
			const deviceData = this.devices[device];
			if (deviceData.battery_capacity && !batteriesCapacities.includes(deviceData.battery_capacity))
				batteriesCapacities.push(deviceData.battery_capacity);

			if (deviceData.battery_voltage && !batteriesVoltages.includes(deviceData.battery_voltage))
				batteriesVoltages.push(deviceData.battery_voltage);
		}

		this.batteryVoltagesOptions = this.commonService.prepareOptions(batteriesVoltages);
		this.batteryCapacitiesOptions = this.commonService.prepareOptions(batteriesCapacities);
	}

	formatSelectedData() {
		const selectedData = this.data?.[this.selectedPeriod]?.[this.selectedDaysFilter];

		if (!this.chartType)
			return this.value =  Math.round(selectedData.totalSiteUsageScore || 0);

		switch (this.chartType) {
			case 'fleet_utilization_score': {
				let totalScore = 0;
				let devicesCount = 0;

				for (const device of selectedData?.devicesUsageScore || []) {
					if (!this.isIncludedDevice(device.macAddress))
						continue;

					totalScore += device.score;
					devicesCount++;
				}

				if (!devicesCount)
					return this.value = 0;

				this.value = Math.round(totalScore / devicesCount);
				break;
			}
			case 'fleet_energy_usage_score': {
				let totalScore = 0;
				let devicesCount = 0;

				for (const device of selectedData?.devicesUsageScore || []) {
					if (!this.isIncludedDevice(device.macAddress))
						continue;

					totalScore += device.energy_score;
					devicesCount++;
				}

				if (!devicesCount)
					return this.value = 0;

				this.value = Math.round(totalScore / devicesCount);
				break;
			}
			case 'battery-ah-return-score':
				let totalDevicesInuseAh = 0;
				let totalDevicesChargeAh = 0;
				for (const device of selectedData?.batteryAhScoreList || []) {
					if (!this.isIncludedDevice(device.macAddress))
						continue;

					totalDevicesInuseAh += device.inuseAh;
					totalDevicesChargeAh += device.chargerAh;
				}

				if (!totalDevicesInuseAh || !totalDevicesChargeAh)
					return this.value = 0;

				this.value = Math.round((totalDevicesChargeAh / totalDevicesInuseAh) * 100);
				break;
			default:
				break;
		}
	}

	isIncludedDevice(macAddress: string) {
		if (this.hasTruckTypeFilter && this.selectedTruckType !== 'all' && this.devices?.[macAddress]?.truck_type !== this.selectedTruckType)
			return false;

		if (this.hasVoltageFilter && this.selectedBatteryVoltage !== 'all' && this.devices?.[macAddress]?.battery_voltage !== this.selectedBatteryVoltage)
			return false;

		if (this.hasCapacityFilter && this.selectedBatteryCapacity !== 'all' && this.devices?.[macAddress]?.battery_capacity !== this.selectedBatteryCapacity)
			return false;

		return true;
	}

	updateChart() {
		const maxValue = this.ranges[this.ranges.length - 1].to;

		if (this.value < 0) {
			this.value = 0;
		} else if (this.value >= this.chartMaxValue) {
			this.value = this.chartMaxValue;
			this.valueStatus = this.ranges[this.ranges.length - 1].status;
			this.statusColor = this.ranges[this.ranges.length - 1].color;
			this.textColor = this.ranges[this.ranges.length - 1].textColor;
			this.valuePx = this.value * this.chartWidthPx / maxValue;
			return;
		}

		this.valuePx = this.value * this.chartWidthPx / maxValue;
		this.valueStatus = this.ranges.find(range => range.from <= this.value && range.to > this.value).status;
		this.statusColor = this.ranges.find(range => range.from <= this.value && range.to > this.value).color;
		this.textColor = this.ranges.find(range => range.from <= this.value && range.to > this.value).textColor;
	}
}
